// src/components/blog/admin/BlogAdminDashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { db } from '../../../firebase';
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  where,
  doc,
  deleteDoc
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../contexts/AuthContext';
import BlogPostEditor from './BlogPostEditor';
import BlogPostList from './BlogPostList';
import ConfirmDeleteModal from '../../ConfirmDeleteModal';

function BlogAdminDashboard() {
  const { currentUser, userRole } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, postId: null, postTitle: '' });

  // Fetch all posts (including drafts) for the admin
  const fetchAllPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const postsQuery = query(
        collection(db, 'blog_posts'),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(postsQuery);
      
      const fetchedPosts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        publishDate: doc.data().publishDate?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      
      setPosts(fetchedPosts);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts. Please try again.');
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  // Delete a post
  const handleDeletePost = async (postId) => {
    try {
      await deleteDoc(doc(db, 'blog_posts', postId));
      setPosts(posts.filter(post => post.id !== postId));
      toast.success('Post deleted successfully');
    } catch (err) {
      console.error('Error deleting post:', err);
      toast.error('Failed to delete post');
    } finally {
      setDeleteModal({ isOpen: false, postId: null, postTitle: '' });
    }
  };

  // Confirm delete modal handlers
  const openDeleteModal = (postId, postTitle) => {
    setDeleteModal({ isOpen: true, postId, postTitle });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, postId: null, postTitle: '' });
  };

  // Load posts on component mount
  useEffect(() => {
    fetchAllPosts();
  }, []);

  // Only allow admin access
  if (userRole !== 'admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
        <p className="text-gray-600">Create, edit and manage blog posts</p>
      </header>

      <nav className="mb-8 bg-white p-4 rounded shadow">
        <ul className="flex space-x-6">
          <li>
            <NavLink 
              to="/blog/admin" 
              end
              className={({ isActive }) => 
                isActive ? 'text-blue-500 font-semibold' : 'text-gray-600 hover:text-blue-500 font-medium'
              }
            >
              All Posts
            </NavLink>
          </li>
          <li>
            <NavLink 
              to="/blog/admin/new" 
              className={({ isActive }) => 
                isActive ? 'text-blue-500 font-semibold' : 'text-gray-600 hover:text-blue-500 font-medium'
              }
            >
              New Post
            </NavLink>
          </li>
          <li>
            <NavLink 
              to="/blog" 
              className="text-gray-600 hover:text-blue-500 font-medium"
            >
              View Blog
            </NavLink>
          </li>
        </ul>
      </nav>

      <Routes>
        <Route 
          path="/" 
          element={
            <BlogPostList 
              posts={posts} 
              loading={loading} 
              error={error} 
              onRefresh={fetchAllPosts}
              onDeleteClick={openDeleteModal}
            />
          } 
        />
        <Route path="/new" element={<BlogPostEditor onSaved={fetchAllPosts} />} />
        <Route path="/edit/:postId" element={<BlogPostEditor onSaved={fetchAllPosts} />} />
        <Route path="*" element={<Navigate to="/blog/admin" replace />} />
      </Routes>

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        title="Delete Blog Post"
        message={`Are you sure you want to delete "${deleteModal.postTitle}"? This action cannot be undone.`}
        onConfirm={() => handleDeletePost(deleteModal.postId)}
        onCancel={closeDeleteModal}
      />
    </div>
  );
}

export default BlogAdminDashboard;